const TEST_FILE_PATTERN = /\.(test|spec)\./;

export interface AgentPersona {
  id: string;
  label: string;
  /** Shown in the persona picker's option list. */
  description: string;
  /** Appended as an extra system block alongside the normal pipeline-assembled context. */
  systemPromptAddition: string;
  allowedTools: string[];
  /** Extra guard on write_file's target path, beyond just allowedTools including "write_file". */
  canWritePath?: (path: string) => boolean;
}

export const AGENT_PERSONAS: AgentPersona[] = [
  {
    id: 'general',
    label: 'General',
    description: 'Full read/write access to the workspace.',
    systemPromptAddition:
      'You have tools to read, list, search, and write files in the workspace. Use them as needed to accomplish the task, then give a concise final summary of what you did.',
    allowedTools: ['read_file', 'list_files', 'search_files', 'write_file'],
  },
  {
    id: 'reviewer',
    label: 'Code Reviewer',
    description: 'Read-only — reviews and critiques, never edits.',
    systemPromptAddition:
      'You are reviewing code for correctness, clarity, and simplification opportunities. You have read-only tools (read_file, list_files, search_files) — you have no way to write files, so give your findings as your final text response rather than attempting to fix anything.',
    allowedTools: ['read_file', 'list_files', 'search_files'],
  },
  {
    id: 'test-writer',
    label: 'Test Writer',
    description: 'Can only write files that look like tests (path contains ".test." or ".spec.").',
    systemPromptAddition:
      'You write tests. You may read any file, but write_file will refuse any path that isn\'t a test file (must contain ".test." or ".spec."). If a test file for what you need doesn\'t exist yet, say so in your final response rather than writing to a non-test file.',
    allowedTools: ['read_file', 'list_files', 'search_files', 'write_file'],
    canWritePath: (path) => TEST_FILE_PATTERN.test(path),
  },
  {
    id: 'refactorer',
    label: 'Refactorer',
    description: 'Can edit source files, but write_file refuses test files.',
    systemPromptAddition:
      'You refactor code for clarity and simplicity without changing behavior. You may read any file, but write_file will refuse any test file (path containing ".test." or ".spec.") — tests define the behavior you must preserve, so you must never alter them.',
    allowedTools: ['read_file', 'list_files', 'search_files', 'write_file'],
    canWritePath: (path) => !TEST_FILE_PATTERN.test(path),
  },
];

export function getPersona(id: string): AgentPersona {
  return AGENT_PERSONAS.find((p) => p.id === id) ?? AGENT_PERSONAS[0];
}
